import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RanchService } from '../../service';
import { IRanchWellViewModel } from '../../interfaces';
import { SharedKeepDiscardDialog } from '../../../shared/dialogs/keep-discard-dialog';
import { SharedUpdateService } from '../../../shared/dialogs/update.service';

type RanchWellFields = 'Name' | 'ElectricalConductivity' | 'NitrogenPPM';

@Component({
	moduleId: module.id,
	selector: 'ranch-well-form',
	templateUrl: 'main.html'
})

export class RanchWellFormComponent implements OnInit, OnDestroy {
	@Input()
	public id: number;

	@Input()
	public well: IRanchWellViewModel;

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onHide: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onShow: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onSubmit: EventEmitter<IRanchWellViewModel> = new EventEmitter<IRanchWellViewModel>();

	public fields: { [key in RanchWellFields]: RanchWellFields } = {
		Name: 'Name',
		ElectricalConductivity: 'ElectricalConductivity',
		NitrogenPPM: 'NitrogenPPM'
	};
	public form: FormGroup;
	public loadingData = true;
	public wellName: string;

	private _subscriptions$: Subject<boolean>;

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder,
		private _ranchService: RanchService,
		private _sharedUpdateService: SharedUpdateService
	) { }

	public get f(): { [key in RanchWellFields]: AbstractControl } {
		return this.form ? this.form.controls as { [key in RanchWellFields]: AbstractControl } : null;
	}

	public get isValid(): boolean {
		return this.form && this.form.valid;
	}

	public ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._initializeForm();
		this._setupPopupSubscriptions();

		if (this.well) {
			this._setFormValue(this.well);
			this.loadingData = false;
		} else if (this.id) {
			this._getEditWellData(this.id);
		} else {
			this.loadingData = false;
		}
	}

	public ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public cancel(): void {
		if (this.form && this.form.touched) {
			this._dialog.open(SharedKeepDiscardDialog, {
				width: '500px',
				disableClose: true,
			});

			this.onHide.emit();
		} else {
			this.onCancel.emit();
		}
	}

	public submit(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this.onSubmit.emit(this._buildWell());
	}

	private _buildWell(): IRanchWellViewModel {
		return {
			Id: this.id ? this.id : (this.well ? this.well.Id : null),
			Name: this.form.get(this.fields.Name).value,
			ElectricalConductivity: this.form.get(this.fields.ElectricalConductivity).value,
			NitrogenPPM: this.form.get(this.fields.NitrogenPPM).value
		};
	}

	private _getEditWellData(id: number): void {
		this._ranchService.getRanchWellEditData(id)
			.then(data => {
				this.form.markAsDirty();
				this._setFormValue(data);
				this.loadingData = false;
			});
	}

	private _initializeForm(): void {
		this.form = this._fb.group({
			Name: [null, [Validators.required, Validators.maxLength(128)]],
			ElectricalConductivity: [null, [Validators.min(0.00001),
				Validators.max(2147483627)]],
			NitrogenPPM: [null, [Validators.required, Validators.min(0.0000001),
				Validators.max(2147483627)]]
		});
	}

	private _setFormValue(well: IRanchWellViewModel): void {
		this.form.get(this.fields.Name).setValue(well.Name);
		this.form.get(this.fields.ElectricalConductivity).setValue(well.ElectricalConductivity);
		this.form.get(this.fields.NitrogenPPM).setValue(well.NitrogenPPM);
		this.wellName = well.Name;
	}

	private _setupPopupSubscriptions(): void {
		this._sharedUpdateService.keep$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onShow.emit();
		});

		this._sharedUpdateService.discard$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.onCancel.emit();
		});
	}
}
